import { call, put, all, takeLatest, select } from 'redux-saga/effects'
import { addAddonItems } from './addOnItems'
import { addOnCategories } from './addOnCategories'
import { addSettings } from 'portal-modules/common/redux/modules/settings'
import { normalize, schema } from 'normalizr'
import {
  getRentableItems,
  saveRentableItems,
} from 'portal-modules/rentable-items/api/api'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import { requestRentedItems } from './rentedItems'

// action types
const REQUEST_RENTABLE_ITEMS = 'rp/rentableItems/REQUEST_RENTABLE_ITEMS'
const RECEIVE_RENTABLE_ITEMS = 'rp/rentableItems/RECEIVE_RENTABLE_ITEMS'
const SAVE_NEW_ITEMS = 'rp/rentableItems/SAVE_RENTABLE_ITEMS'

// reducer
export default (state = {}, { type, payload }) => {
  switch (type) {
    case RECEIVE_RENTABLE_ITEMS:
      return payload
    default:
      return state
  }
}

// action creators
export const requestRentableItems = () => ({ type: REQUEST_RENTABLE_ITEMS })
export const receiveRentableItems = payload => ({
  type: RECEIVE_RENTABLE_ITEMS,
  payload,
})

export const addRentableItems = (payload, callback) => ({
  type: SAVE_NEW_ITEMS,
  callback,
  payload,
})

// normalize functionality
const addOnItem = new schema.Entity('addOnItems')
const rentableItem = new schema.Entity('rentableItems', {
  add_on_items: [addOnItem],
})

const normalizeAddOnItems = data => normalize(data, [rentableItem]).entities

const buildSaveRequestItems = ({ id, items }) => {
  let request = items.reduce((newRequest, itemId) => {
    let itemObj = { rentable_item_id: id, rentable_add_on_id: itemId }
    newRequest.push(itemObj)

    return newRequest
  }, [])

  request = request.length ? request : [{ rentable_item_id: id }]

  return request
}

// sagas
function* fetchRentableItems() {
  const {
    rentable_items,
    settings: { categories, property_map },
  } = yield call(getRentableItems)

  let { addOnItems, rentableItems } = normalizeAddOnItems(rentable_items)

  // need to split out the data to all its appropriate locations
  yield all([
    rentableItems && put(receiveRentableItems(rentableItems)),
    addOnItems && put(addAddonItems(addOnItems)),
    put(
      addOnCategories(
        Object.values(categories).reduce(
          (obj, cat) => ({ ...obj, [cat.id]: cat }),
          {},
        ),
      ),
    ),
    put(addSettings({ propertyMap: property_map })),
  ])
}

function* watchRequestedRentableItems() {
  yield takeLatest(REQUEST_RENTABLE_ITEMS, function* (action) {
    const { isLeaseAndRentWordReplaced } = yield select(
      ({ productPermissions }) => ({
        isLeaseAndRentWordReplaced:
          productPermissions?.replace_lease_and_rent || false,
      }),
    )
    
    const dynamicMessage = isLeaseAndRentWordReplaced
      ? __('Finding available items')
      : __('Finding rentable items')

    yield* createRequestSaga(fetchRentableItems, {
      key: 'rentable_items',
      message: dynamicMessage,
    })(action)
  })
}

function* postAddRentableItems({ payload }) {
  const requestItems = buildSaveRequestItems(payload)
  yield call(saveRentableItems, {
    rent_items: requestItems,
  })

  yield put(requestRentedItems())
  yield put(requestRentableItems())
}

function* watchAddRentableItems() {
  yield takeLatest(
    SAVE_NEW_ITEMS,
    createRequestSaga(postAddRentableItems, {
      key: 'save_rentable_items',
      message: __('Saving Rentable Item Preference'),
      successMessage: __('Your request has been submitted.'),
      write: true,
    }),
  )
}

export const sagas = [watchRequestedRentableItems, watchAddRentableItems]
